// Tile map, circle-vs-tile collision and BFS flow fields. Pure and DOM/Node free: the server and the browser both import it.

export interface Vec { x: number; y: number }

export interface MapJson {
  name: string;
  tile: number;
  w: number;
  h: number;
  legend: Record<string, string>;
  solid: string[];
  plaza: { x: number; y: number; r: number };
  gates: { name: string; kind: 'main' | 'side'; x: number; y: number; nx: number; ny: number }[];
  spawnsP: [number, number][];
  spawnsC: [number, number][];
  pockets: [number, number][];
  hides: [number, number][];
  rows: string[];
  /** metres per tile. Absent on hand-made maps, which keep the legacy px constants (see speedFor in sim.ts) */
  mpt?: number;
  /** lat/lon of the north-west corner, for maps converted from OSM */
  origin?: { lat: number; lon: number };
  /** w*h building heights in metres, row-major; 0 = no building. For the 2.5D renderer */
  heights?: number[];
  /** width in tiles of the border band drawn just outside the map edge; absent = no border */
  border?: number;
  /** surround maps only (tools/make-surround.mjs): the margin in tiles, so tile (inset, inset) is the play map's (0, 0) */
  inset?: number;
  /** how the client draws roads (from the spec's markings section) */
  markings?: Markings;
  /** drawn roads: class, width in metres, OSM name if any, oneway, roundabout, centreline as flat x,y pairs in tile units */
  roadLines?: { c: string; w: number; n?: string; o?: 1; r?: 1; l: number[] }[];
  /** [x, y, radius] in tile units: road vertices where 3 or more arms meet */
  junctions?: [number, number, number][];
  /** OSM footway=crossing ways, flat x,y pairs in tile units */
  crossings?: number[][];
  /** inferred traffic signals: [x, y, facing angle] in tile units */
  signals?: [number, number, number][];
  /** the protest zone as polygons in tile units */
  zone?: [number, number][][];
  /** sprite names that trees[i][2] indexes */
  treeKinds?: string[];
  /** trees and bushes, drawn not solid: [x, y (tile units, the trunk's foot), kind index, scale] */
  trees?: [number, number, number, number][];
  /** names that buildings[i].s indexes (the spec's buildings.styles) */
  buildingStyles?: string[];
  /** sprite names that buildings[i].p[j][2] indexes */
  roofKinds?: string[];
  /** footprints for the 2.5D renderer, one per outer ring; drawn only, collision stays on the tiles */
  buildings?: Building[];
  /** metro station entrances on their pads (osm-to-map, spec.stations), in tile units */
  stations?: Station[];
  /** labels for the poi groups, keyed by group id */
  poiGroups?: Record<string, string>;
  /** every tagged OSM node in the window, in tile units: group id, name if any, the tag that placed it, its OSM id */
  pois?: Poi[];
  /**
   * walled compounds nobody may enter (<map>.nogo.json, tools/nogo.py), as [y, x0, x1] runs of tiles: solid, though the
   * rows still say ground. Added by withNogo(); a map file never carries it.
   */
  nogo?: number[][];
  /** the compound kinds the runs' 4th value indexes; paved open ground; the boundary walls, their gates and guard posts */
  compoundKinds?: string[];
  paved?: number[][];
  walls?: NogoJson['walls'];
  corners?: number[][];
  wallGates?: NogoJson['gates'];
  guards?: NogoJson['guards'];
  /** provenance: source file, its hash, the credit line to show, and the OSM ids behind each marker list */
  meta?: { source: string; sha256: string; attribution?: string; markers: Record<string, string[]> };
}

export interface Markings {
  /** pavement drawn each side, beyond the kerb (absent: none) */
  pavementM?: number;
  kerbM: number; edgeLineM: number; edgeInsetM: number; edgeClasses: string[];
  centreDashM: [number, number]; centreLineM: number; centreMinWidthM: number; centreClasses: string[];
  zebra: { widthM: number; barM: number; gapM: number };
  signals: { minArms: number; minMainArms: number; mainClasses: string[]; setbackM: number; stopLineM: number; scale: number };
}

/** style index, height in metres, outer ring and holes as flat x,y pairs in tile units (not closed), roof props [x, y, kind] */
export interface Building {
  s: number; h: number; o: number[]; i?: number[][]; p?: [number, number, number][];
  /** name: from OSM (the block's largest named building) or made up for a generated shop */
  n?: string;
  /** shops: unit vector from the shop towards its road, so its front gets the shopfront */
  f?: [number, number];
}

export interface Poi { x: number; y: number; g: string; name?: string; tag: string; osm: string }

/**
 * A metro entrance: its foot (tile units), the sprite key (its turn faces the mouth to the nearest road), the scale it is
 * drawn at (times life size), the station's name in English and Hindi for the sign over it, and the OSM nodes it serves.
 */
export interface Station { x: number; y: number; k: string; s: number; n?: string; hi?: string; osm: string[] }

export interface GameMap {
  name: string;
  tile: number;
  w: number;
  h: number;
  /** world size in px */
  width: number;
  height: number;
  rows: string[];
  solid: Uint8Array; // 1 = solid tile
  plaza: { x: number; y: number; r: number }; // px
  gates: { name: string; kind: 'main' | 'side'; x: number; y: number; nx: number; ny: number }[]; // px
  spawnsP: Vec[];
  spawnsC: Vec[];
  pockets: Vec[];
  hides: Vec[];
  mpt?: number;
  origin?: { lat: number; lon: number };
  heights?: number[];
  border?: number;
  markings?: Markings;
  /** road drawing data, in tile units as in the file */
  roadLines?: MapJson['roadLines'];
  junctions?: MapJson['junctions'];
  crossings?: MapJson['crossings'];
  signals?: MapJson['signals'];
  zone?: MapJson['zone'];
  treeKinds?: string[];
  /** in tile units, as in the file */
  trees?: [number, number, number, number][];
  buildingStyles?: string[];
  roofKinds?: string[];
  /** in tile units, as in the file */
  buildings?: Building[];
  /** in tile units, as in the file */
  stations?: Station[];
  poiGroups?: Record<string, string>;
  /** in px, like the other markers */
  pois: Poi[];
  /** credit line that must be shown wherever the map is (OSM's ODbL), if the map came from OSM */
  attribution?: string;
  /** 1 on a walled compound tile (also solid); null when the map has no no-go overlay */
  nogo: Uint8Array | null;
  /** per tile: the compound's kind index + 1 into compoundKinds (which wall it gets), 0 off the compounds */
  compound: Uint8Array | null;
  compoundKinds: string[];
  /** per tile: 1 on open ground that is not a road's right of way (driveways, markers' patches, parks): paved, not lawn */
  paved: Uint8Array | null;
  /** boundary walls in world px (each point pair a segment), the compound's side, piers and gates as px along the run */
  walls: Wall[];
  /** the pavement a rounded block corner gives back to the street: polygons in world px (flat x, y pairs) */
  corners: number[][];
  /** per tile: the wall segments that may touch a body there, as [wall, segment] index pairs */
  wallsAt: Map<number, number[]>;
  guards: NonNullable<NogoJson['guards']>;
  wallGates: NonNullable<NogoJson['gates']>;
  fields: Map<string, Int16Array>;
}

/** <map>.nogo.json (tools/nogo.py) */
export interface NogoJson {
  tiles: number;
  /** [y, x0, x1, kind]: a row's run of compound tiles of one kind (kind indexes kinds), wholly behind a wall: solid */
  runs: number[][];
  kinds?: string[];
  /** [y, x0, x1]: open ground that is not a road's right of way */
  paved?: number[][];
  /**
   * boundary walls (docs/plans/2026-10-18-street-geometry.md), tile units: k the kind, p the points, out the side of the
   * line the compound is on (1: left of its direction), piers as distances along it, road the roadLines index (-1: a
   * tile edge against paved ground)
   */
  walls?: { k: number; p: number[]; out: 1 | -1; piers: number[]; road: number; corner?: 1 }[];
  /** rounded block corners: the old corner point, then the arc that replaced it, tile units (paved, not lawn) */
  corners?: number[][];
  /** a gate in a wall, and a guard post at a government site's gate: a wall and a distance along it, in tiles */
  gates?: { run: number; at: number; from: string }[];
  guards?: { name: string; run: number; at: number }[];
}

/** A boundary wall in world px: its kind, its points, the side the compound is on, piers along it (px from its start). */
export interface Wall { k: number; p: number[]; out: 1 | -1; piers: number[]; road: number; corner?: 1 }
/** How far a wall stops a body short of its line, metres: half its thickness. */
export const WALL_HALF_M = 0.4;

/** The map with its walled compounds (docs/plans/2026-10-18-no-go.md): what the server plays and serves as /map.json. */
export const withNogo = (j: MapJson, n: NogoJson): MapJson =>
  ({ ...j, nogo: n.runs, compoundKinds: n.kinds, paved: n.paved, walls: n.walls, corners: n.corners, wallGates: n.gates, guards: n.guards });

export function loadMap(j: MapJson): GameMap {
  if (j.rows.length !== j.h || j.rows.some((r) => r.length !== j.w)) throw new Error('map rows do not match w/h');
  if (j.heights && j.heights.length !== j.w * j.h) throw new Error('map heights do not match w*h');
  const solidChars = new Set(j.solid);
  const solid = new Uint8Array(j.w * j.h);
  for (let y = 0; y < j.h; y++) for (let x = 0; x < j.w; x++) if (solidChars.has(j.rows[y][x])) solid[y * j.w + x] = 1;
  const nogo = j.nogo ? new Uint8Array(j.w * j.h) : null;
  const compound = j.nogo ? new Uint8Array(j.w * j.h) : null;
  for (const [y, x0, x1, k] of j.nogo ?? []) for (let x = x0; x <= x1; x++) { nogo![y * j.w + x] = 1; solid[y * j.w + x] = 1; compound![y * j.w + x] = (k ?? 0) + 1; }
  const paved = j.paved ? new Uint8Array(j.w * j.h) : null;
  for (const [y, x0, x1] of j.paved ?? []) for (let x = x0; x <= x1; x++) paved![y * j.w + x] = 1;
  // walls to world px, and each segment filed under every tile a body touching it could stand on
  const walls: Wall[] = (j.walls ?? []).map((w) => ({ ...w, p: w.p.map((v) => v * j.tile), piers: w.piers.map((v) => v * j.tile) }));
  const wallsAt = new Map<number, number[]>();
  walls.forEach((w, wi) => {
    for (let i = 0; i + 3 < w.p.length; i += 2) {
      const pad = 1; // a tile: wider than any body
      const tx0 = Math.floor(Math.min(w.p[i], w.p[i + 2]) / j.tile) - pad, tx1 = Math.floor(Math.max(w.p[i], w.p[i + 2]) / j.tile) + pad;
      const ty0 = Math.floor(Math.min(w.p[i + 1], w.p[i + 3]) / j.tile) - pad, ty1 = Math.floor(Math.max(w.p[i + 1], w.p[i + 3]) / j.tile) + pad;
      for (let ty = Math.max(0, ty0); ty <= Math.min(j.h - 1, ty1); ty++) for (let tx = Math.max(0, tx0); tx <= Math.min(j.w - 1, tx1); tx++) {
        const k = ty * j.w + tx, list = wallsAt.get(k);
        if (list) list.push(wi, i / 2); else wallsAt.set(k, [wi, i / 2]);
      }
    }
  });
  const t = j.tile;
  const px = (p: [number, number]): Vec => ({ x: p[0] * t, y: p[1] * t });
  return {
    name: j.name, tile: t, w: j.w, h: j.h, width: j.w * t, height: j.h * t, rows: j.rows, solid,
    plaza: { x: j.plaza.x * t, y: j.plaza.y * t, r: j.plaza.r * t },
    gates: j.gates.map((g) => ({ ...g, x: g.x * t, y: g.y * t })),
    spawnsP: j.spawnsP.map(px), spawnsC: j.spawnsC.map(px), pockets: j.pockets.map(px), hides: j.hides.map(px),
    mpt: j.mpt, origin: j.origin, heights: j.heights, attribution: j.meta?.attribution, nogo, compound, compoundKinds: j.compoundKinds ?? [], paved, walls, wallsAt, corners: (j.corners ?? []).map((c) => c.map((v) => v * j.tile)), guards: j.guards ?? [], wallGates: j.wallGates ?? [],
    border: j.border, markings: j.markings, roadLines: j.roadLines, junctions: j.junctions, crossings: j.crossings, signals: j.signals, zone: j.zone,
    treeKinds: j.treeKinds, trees: j.trees, buildingStyles: j.buildingStyles, roofKinds: j.roofKinds, buildings: j.buildings, stations: j.stations, poiGroups: j.poiGroups, pois: (j.pois || []).map((p) => ({ ...p, x: p.x * t, y: p.y * t })),
    fields: new Map(),
  };
}

export const solidAt = (m: GameMap, tx: number, ty: number): boolean =>
  tx < 0 || ty < 0 || tx >= m.w || ty >= m.h || m.solid[ty * m.w + tx] === 1;

const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);

/** Push a circle out of solid tiles (and keep it inside the world). Mutates p. With a mask, tiles outside it are solid too. */
export function collide(m: GameMap, p: Vec, r: number, mask?: Uint8Array): void {
  const t = m.tile;
  for (let iter = 0; iter < 3; iter++) {
    let moved = false;
    // boundary walls first (docs/plans/2026-10-18-street-geometry.md): segments at any angle; a body keeps half the
    // wall's thickness plus its radius from the line, on whichever side it is. Solid tiles after, so they have the last word
    const near = m.wallsAt.get(Math.floor(p.y / t) * m.w + Math.floor(p.x / t));
    if (near) {
      const rr = r + WALL_HALF_M * (m.mpt ? t / m.mpt : 1);
      for (let n = 0; n < near.length; n += 2) {
        const w = m.walls[near[n]].p, i = near[n + 1] * 2;
        const x1 = w[i], y1 = w[i + 1], dx = w[i + 2] - x1, dy = w[i + 3] - y1, L2 = dx * dx + dy * dy;
        const u = L2 ? clamp(((p.x - x1) * dx + (p.y - y1) * dy) / L2, 0, 1) : 0;
        const ex = p.x - (x1 + u * dx), ey = p.y - (y1 + u * dy), dd = ex * ex + ey * ey;
        if (dd >= rr * rr) continue;
        if (dd > 1e-9) { const d = Math.sqrt(dd); p.x += (ex / d) * (rr - d); p.y += (ey / d) * (rr - d); }
        else if (L2) { const L = Math.sqrt(L2); p.x += (-dy / L) * rr; p.y += (dx / L) * rr; } // on the line: step off it
        moved = true;
      }
    }
    const x0 = Math.floor((p.x - r) / t), x1 = Math.floor((p.x + r) / t);
    const y0 = Math.floor((p.y - r) / t), y1 = Math.floor((p.y + r) / t);
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (!solidAt(m, tx, ty) && (!mask || mask[ty * m.w + tx])) continue;
        const nx = clamp(p.x, tx * t, (tx + 1) * t), ny = clamp(p.y, ty * t, (ty + 1) * t);
        const dx = p.x - nx, dy = p.y - ny, dd = dx * dx + dy * dy;
        if (dd >= r * r) continue;
        if (dd > 1e-6) {
          const d = Math.sqrt(dd), push = r - d;
          p.x += (dx / d) * push; p.y += (dy / d) * push;
        } else { // centre is inside the tile: leave through the nearest face
          const l = p.x - tx * t, rt = (tx + 1) * t - p.x, tp = p.y - ty * t, bt = (ty + 1) * t - p.y, mn = Math.min(l, rt, tp, bt);
          if (mn === l) p.x = tx * t - r; else if (mn === rt) p.x = (tx + 1) * t + r; else if (mn === tp) p.y = ty * t - r; else p.y = (ty + 1) * t + r;
        }
        moved = true;
      }
    }
    if (!moved) break;
  }
  p.x = clamp(p.x, r, m.width - r);
  p.y = clamp(p.y, r, m.height - r);
}

export interface Body extends Vec { vx: number; vy: number; r: number }

/**
 * One movement step, shared by the server sim and client prediction so both compute the same result.
 * (wx, wy) is the desired direction (length <= 1), spd in px/s; mask, if given, is the play area (see collide).
 * Returns the distance actually moved.
 */
export function moveBody(m: GameMap, p: Body, wx: number, wy: number, spd: number, dt: number, mask?: Uint8Array): number {
  const k = Math.min(1, dt * 12);
  p.vx += (wx * spd - p.vx) * k;
  p.vy += (wy * spd - p.vy) * k;
  const ox = p.x, oy = p.y;
  p.x += p.vx * dt; p.y += p.vy * dt;
  collide(m, p, p.r, mask);
  return Math.hypot(p.x - ox, p.y - oy);
}

// ---------------------------------------------------------------- flow fields (tile resolution)

/** BFS distance (in tiles) from the target points; -1 where unreachable or solid (or outside `within`, if given). */
export function buildField(m: GameMap, targets: Vec[], within?: Uint8Array): Int16Array {
  const S = within ? m.solid.map((v, i) => v | (within[i] ? 0 : 1)) : m.solid;
  const F = new Int16Array(m.w * m.h).fill(-1);
  const q = new Int32Array(m.w * m.h);
  let h = 0, tl = 0;
  for (const t of targets) {
    const tx = Math.floor(t.x / m.tile), ty = Math.floor(t.y / m.tile);
    if (solidAt(m, tx, ty) || S[ty * m.w + tx]) continue;
    const i = ty * m.w + tx;
    if (F[i] < 0) { F[i] = 0; q[tl++] = i; }
  }
  while (h < tl) {
    const i = q[h++], x = i % m.w, y = (i / m.w) | 0, v = F[i] + 1;
    if (x > 0 && F[i - 1] < 0 && !S[i - 1]) { F[i - 1] = v; q[tl++] = i - 1; }
    if (x < m.w - 1 && F[i + 1] < 0 && !S[i + 1]) { F[i + 1] = v; q[tl++] = i + 1; }
    if (y > 0 && F[i - m.w] < 0 && !S[i - m.w]) { F[i - m.w] = v; q[tl++] = i - m.w; }
    if (y < m.h - 1 && F[i + m.w] < 0 && !S[i + m.w]) { F[i + m.w] = v; q[tl++] = i + m.w; }
  }
  return F;
}

/** Cached field keyed by a caller-chosen string (e.g. "pocket:3"); the key must also name `within`, if one is given. */
export function fieldFor(m: GameMap, key: string, targets: () => Vec[], within?: Uint8Array): Int16Array {
  let F = m.fields.get(key);
  if (!F) { F = buildField(m, targets(), within); m.fields.set(key, F); }
  return F;
}

export const fieldAt = (m: GameMap, F: Int16Array, x: number, y: number): number => {
  const tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
  return tx < 0 || ty < 0 || tx >= m.w || ty >= m.h ? -1 : F[ty * m.w + tx];
};

/** Unit vector towards the best neighbouring tile, or null when standing on an unreachable tile. */
export function flowDir(m: GameMap, F: Int16Array, x: number, y: number): Vec | null {
  const tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
  if (tx < 0 || ty < 0 || tx >= m.w || ty >= m.h) return null;
  const c = F[ty * m.w + tx];
  if (c < 0) return null;
  let best = c, bx = -1, by = -1;
  for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) {
    if (!a && !b) continue;
    const nx = tx + a, ny = ty + b;
    if (nx < 0 || ny < 0 || nx >= m.w || ny >= m.h) continue;
    const v = F[ny * m.w + nx];
    if (v < 0 || v >= best) continue;
    if (a && b && (solidAt(m, tx + a, ty) || solidAt(m, tx, ty + b))) continue; // no corner cutting
    best = v; bx = nx; by = ny;
  }
  if (bx < 0) return { x: 0, y: 0 }; // at the target
  const dx = (bx + 0.5) * m.tile - x, dy = (by + 0.5) * m.tile - y, d = Math.hypot(dx, dy) || 1;
  return { x: dx / d, y: dy / d };
}

/**
 * Like buildField but octile: 8 neighbours, diagonals cost sqrt 2, no corner cutting (Dijkstra). Distance in tiles,
 * -1 where unreachable or solid. `within`, if given, limits the search to tiles where within[i] is 1.
 */
export function buildOctileField(m: GameMap, targets: number[], within?: Uint8Array): Float32Array {
  const n = m.w * m.h, F = new Float32Array(n).fill(-1), done = new Uint8Array(n);
  const open = (i: number) => !m.solid[i] && (!within || within[i] === 1);
  // binary heap of (distance, tile)
  const hd: number[] = [], hi: number[] = [];
  const push = (d: number, i: number) => {
    let k = hd.length; hd.push(d); hi.push(i);
    while (k > 0) { const p = (k - 1) >> 1; if (hd[p] <= d) break; hd[k] = hd[p]; hi[k] = hi[p]; k = p; }
    hd[k] = d; hi[k] = i;
  };
  const pop = (): number => {
    const top = hi[0], d = hd.pop()!, i = hi.pop()!;
    if (hd.length) {
      let k = 0;
      for (;;) {
        const a = 2 * k + 1, b = a + 1; let c = k, cd = d;
        if (a < hd.length && hd[a] < cd) { c = a; cd = hd[a]; }
        if (b < hd.length && hd[b] < cd) c = b;
        if (c === k) break;
        hd[k] = hd[c]; hi[k] = hi[c]; k = c;
      }
      hd[k] = d; hi[k] = i;
    }
    return top;
  };
  for (const i of targets) if (open(i) && F[i] !== 0) { F[i] = 0; push(0, i); }
  while (hd.length) {
    const i = pop();
    if (done[i]) continue;
    done[i] = 1;
    const x = i % m.w, y = (i / m.w) | 0;
    for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) {
      if (!a && !b) continue;
      const nx = x + a, ny = y + b;
      if (nx < 0 || ny < 0 || nx >= m.w || ny >= m.h) continue;
      const j = ny * m.w + nx;
      if (done[j] || !open(j)) continue;
      if (a && b && (!open(y * m.w + nx) || !open(ny * m.w + x))) continue; // no corner cutting
      const d = F[i] + (a && b ? Math.SQRT2 : 1);
      if (F[j] < 0 || d < F[j]) { F[j] = d; push(d, j); }
    }
  }
  return F;
}

/** Unit vector downhill on an octile field (as flowDir does on a BFS field); null off the field, 0 at a target. */
export function octileDir(m: GameMap, F: Float32Array, x: number, y: number): Vec | null {
  const tx = Math.floor(x / m.tile), ty = Math.floor(y / m.tile);
  if (tx < 0 || ty < 0 || tx >= m.w || ty >= m.h || F[ty * m.w + tx] < 0) return null;
  const j = step(m, F, ty * m.w + tx);
  if (j < 0) return { x: 0, y: 0 };
  const dx = ((j % m.w) + 0.5) * m.tile - x, dy = (((j / m.w) | 0) + 0.5) * m.tile - y, d = Math.hypot(dx, dy) || 1;
  return { x: dx / d, y: dy / d };
}

/**
 * The next tile from i on a shortest path down an octile field (no corner cutting), or -1 at a target. Of the
 * neighbours on a shortest path, a straight step wins over a diagonal, so paths follow streets rather than zigzag.
 */
function step(m: GameMap, F: Float32Array, i: number): number {
  if (F[i] <= 0) return -1;
  const x = i % m.w, y = (i / m.w) | 0;
  let bj = -1, bscore = Infinity;
  for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) {
    if (!a && !b) continue;
    const nx = x + a, ny = y + b;
    if (nx < 0 || ny < 0 || nx >= m.w || ny >= m.h) continue;
    const j = ny * m.w + nx;
    if (F[j] < 0 || F[j] >= F[i]) continue;
    if (a && b && (F[y * m.w + nx] < 0 || F[ny * m.w + x] < 0)) continue;
    // slack: how far this step is off a shortest path (0 when it is on one); then straight before diagonal
    const slack = F[j] + (a && b ? Math.SQRT2 : 1) - F[i], score = (slack > 1e-4 ? 10 + slack : 0) + (a && b ? 1 : 0);
    if (score < bscore) { bscore = score; bj = j; }
  }
  return bj;
}

/** Walk downhill on an octile field from tile i to a 0 tile; the tiles visited, i first. */
export function descend(m: GameMap, F: Float32Array, i: number): number[] {
  const out = [i];
  for (let j = step(m, F, i); j >= 0 && out.length < m.w * m.h; j = step(m, F, j)) out.push(j);
  return out;
}

// ---------------------------------------------------------------- stages (data/maps/<map>.stages.json)

export type StageName = 'gather' | 'march' | 'stand';
export const STAGES: StageName[] = ['gather', 'march', 'stand'];

/** The parts of a stages file the game reads (data/maps/jantar-mantar.stages.json explains each). */
export interface Stages {
  zoneName?: string;
  starts: { spawnP: number; name: string; default: boolean; expected?: Record<string, number> }[];
  gather: { radiusM: number; minS: number; maxS: number; crowd: number; arrivalPerS: number };
  march: { corridorHalfWidthTiles: number; crowd: number; speedFactor: number; maxS: number };
  stand: { zonePadM: number; crowd: number; idleS: [number, number] };
  ambient: { count: number };
  bounds: { outsideDim: number; hintAfterS: number };
  jog: { speedMps: number; drainPerS: number; regenPerS: number; regenDelayS: number; minToStart: number };
  zoom: { walkPxPerMetre: number; standPxPerMetre: number; easeS: number; movingMps: number };
  aoi: { radiusM: number };
  /** personalSpaceM: the gap separation keeps between two bodies, on top of their radii (metres) */
  separation: { spatialHashAbove: number; cellPx: number; personalSpaceM: number };
  traffic?: { closeRoadsInStageArea: StageName[]; fadeOutS: number };
}

/** Every stage's play area for one start, as tile masks (1 = inside), each containing the one before it. */
export interface StageMasks {
  gather: Uint8Array; march: Uint8Array; stand: Uint8Array;
  /** the zone and its pad: the part of Stand that is the destination */
  pad: Uint8Array;
  /** octile distance in tiles to the nearest zone (P) tile, over the whole map */
  toZone: Float32Array;
  /** tile indices from the start to the zone, downhill on toZone */
  path: number[];
  start: number;
}

/** The octile field to the zone (every P tile), computed once per map. */
export function zoneField(m: GameMap): Float32Array {
  const c = m as GameMap & { _zoneField?: Float32Array };
  if (!c._zoneField) {
    const zone: number[] = [];
    for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) if (m.rows[y][x] === 'P') zone.push(y * m.w + x);
    c._zoneField = buildOctileField(m, zone);
  }
  return c._zoneField;
}

/** The three play areas for start `startIdx` (an index into st.starts). Cached per map; server and client agree. */
export function stageMasks(m: GameMap, st: Stages, startIdx: number): StageMasks {
  const c = m as GameMap & { _masks?: Map<number, StageMasks> };
  const hit = c._masks?.get(startIdx);
  if (hit) return hit;
  const mpt = m.mpt || 1, n = m.w * m.h, F = zoneField(m);
  const sp = m.spawnsP[st.starts[startIdx].spawnP];
  const sx = sp.x / m.tile, sy = sp.y / m.tile, start = Math.floor(sy) * m.w + Math.floor(sx);
  const gather = new Uint8Array(n), march = new Uint8Array(n), stand = new Uint8Array(n), pad = new Uint8Array(n);
  // gather: reachable tiles whose centre is within the radius of the gate
  const r = st.gather.radiusM / mpt;
  for (let i = 0; i < n; i++) {
    if (F[i] < 0) continue;
    if (Math.hypot((i % m.w) + 0.5 - sx, ((i / m.w) | 0) + 0.5 - sy) <= r) gather[i] = 1;
  }
  // march: gather plus every reachable tile within the corridor's half width (a round brush) of a path tile
  const path = descend(m, F, start), cw = st.march.corridorHalfWidthTiles;
  march.set(gather);
  for (const i of path) {
    const x = i % m.w, y = (i / m.w) | 0;
    for (let b = -cw; b <= cw; b++) for (let a = -cw; a <= cw; a++) {
      if (a * a + b * b > cw * cw) continue;
      const nx = x + a, ny = y + b;
      if (nx >= 0 && ny >= 0 && nx < m.w && ny < m.h && F[ny * m.w + nx] >= 0) march[ny * m.w + nx] = 1;
    }
  }
  // stand: march plus the zone and every reachable tile within the pad of it
  const pr = st.stand.zonePadM / mpt;
  for (let i = 0; i < n; i++) { if (F[i] >= 0 && F[i] <= pr) pad[i] = 1; stand[i] = march[i] | pad[i]; }
  // keep only what can be walked to from the start without leaving the area: a brush reaches behind fences
  for (const k of [gather, march, stand]) connected(m, k, start);
  const out: StageMasks = { gather, march, stand, pad, toZone: F, path, start };
  (c._masks ??= new Map()).set(startIdx, out);
  return out;
}

/** Clear every tile of mask k that is not 4-connected to tile `from` through k. Mutates k. */
function connected(m: GameMap, k: Uint8Array, from: number): void {
  const seen = new Uint8Array(k.length), q = new Int32Array(k.length);
  let h = 0, t = 0;
  if (k[from]) { seen[from] = 1; q[t++] = from; }
  while (h < t) {
    const i = q[h++], x = i % m.w;
    for (const j of [x > 0 ? i - 1 : -1, x < m.w - 1 ? i + 1 : -1, i - m.w, i + m.w]) {
      if (j >= 0 && j < k.length && k[j] && !seen[j]) { seen[j] = 1; q[t++] = j; }
    }
  }
  for (let i = 0; i < k.length; i++) if (!seen[i]) k[i] = 0;
}

export const maskCount = (k: Uint8Array) => k.reduce((s, v) => s + v, 0);
